import { Carousel, useIsWindowBiggerThan } from '@bob/library'
import { mapSelector } from '@websrc/DataMappers/mapSelector'
import { SelectorItem } from '@websrc/Spot/Selector/SelectorItem'
import clsx from 'clsx'
import { breakpoints, gridContainer, gridItem, styles } from 'leds'
import React, { useCallback, useLayoutEffect, useState } from 'react'
import { match } from 'ts-pattern'

import { SelectorList } from './SelectorList'

interface SelectorProps {
  selector: ReturnType<typeof mapSelector>
  className?: string
}

export const Selector: React.FC<SelectorProps> = ({
  selector: { selector_items, variant },
  className,
}) => {
  const [nrOfItemsPerPage, updateNrOfItemsPerPage] = useState(1)
  const isWindowBiggerThan = useIsWindowBiggerThan(breakpoints.m)

  const updateItemsPerPage = useCallback(
    (isWindowSmall: boolean) => {
      if (isWindowSmall) {
        updateNrOfItemsPerPage(1)
      } else {
        updateNrOfItemsPerPage(
          match<number, number>(selector_items.length)
            .with(0, 1, () => 1)
            .with(2, () => 2)
            .otherwise(() => 3)
        )
      }
    },
    [selector_items.length]
  )

  const nrOfColumns = match<number, 'one' | 'two' | 'three'>(selector_items.length)
    .with(0, 1, () => 'one')
    .with(2, () => 'two')
    .otherwise(() => 'three')

  useLayoutEffect(() => {
    updateItemsPerPage(!isWindowBiggerThan)
  }, [updateItemsPerPage, isWindowBiggerThan])

  return match(variant)
    .with('GRID', () => (
      <div
        className={clsx(gridContainer(), styles({ marginY: 50, maxWidth: 960 }), className)}
        data-testid={`grid-${nrOfColumns}`}
      >
        {selector_items.map((item, i) => (
          <div className={clsx(gridItem({ columns: nrOfColumns }), styles({ padding: 5 }))} key={i}>
            <SelectorItem className={styles({ height: 'full' })} content={item} />
          </div>
        ))}
      </div>
    ))
    .with('CAROUSEL', () => (
      <Carousel className={className} displayBlockOnPrint={true} itemsPerPage={nrOfItemsPerPage}>
        {selector_items.map((item, i) => (
          <SelectorItem
            className={styles({ width: 'full' })}
            content={item}
            data-testid={`carousel-${i}-${nrOfItemsPerPage}`}
            key={i}
          />
        ))}
      </Carousel>
    ))
    .with('LIST', () => (
      <SelectorList className={className} items={selector_items} nrOfItemsPerLoad={5} />
    ))
    .exhaustive()
}
